<template>
  <span v-if="disabled">{{dateRangeStr}}</span>
  <div class="date-range-wrapper" v-else>
    <div class="prefix">
      <slot name="prefix">
        <yq-icon class="default-icon" name="element-ui_date"/>
      </slot>
    </div>
    <el-date-picker
        v-model="dateRange"
        type="daterange"
        unlink-panels
        range-separator="至"
        :start-placeholder="startPlaceholder"
        :end-placeholder="endPlaceholder"
        :shortcuts="shortCuts"
        class="date-picker-custom"
        @change="changeData"
    >
    </el-date-picker>
  </div>
</template>

<script>
import YqIcon from "@/components/YqIcon.vue";
import {watch} from "vue";

export default {
  emits: ["update:startTime", "update:endTime", "change", "clear"],
  name: "YqDateRange",
  components: {YqIcon},
  props: {
    disabled: {default: false},
    startTime: {default: ""},
    endTime: {default: ""},
    startPlaceholder: {default: "开始日期"},
    endPlaceholder: {default: "结束日期"},
  },
  created() {
    this.initData();
    watch(() => this.startTime, () => this.initData());
    watch(() => this.endTime, () => this.initData());
  },
  data() {
    return {
      dateRangeStr: "", // 显示的值，只用于显示
      dateRange: [],
      shortCuts: [
        {
          text: '今天',
          value: () => {
            const firstDay = new Date();
            const lastDay = new Date();
            firstDay.setHours(0, 0, 0, 0);
            lastDay.setHours(23, 59, 59, 999);
            return [firstDay, lastDay];
          }
        },
        {
          text: '本周',
          value: () => {
            const today = new Date();
            // 获取本周一（周日为一周开始则为周日）
            const dayOfWeek = today.getDay();
            // 周一为一周开始：周日(0)-周六(6)，周一为1，所以减去 dayOfWeek - 1
            const monday = new Date(today);
            monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
            monday.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return [monday, end];
          }
        },
        {
          text: '本月',
          value: () => {
            const today = new Date();
            // 获取本月第一天
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            firstDay.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return [firstDay, end];
          }
        },
        {
          text: '三个月内',
          value: () => {
            const today = new Date();
            // 获取三个月前的月份的第一天
            const startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
            startDate.setHours(0, 0, 0, 0);
            const end = new Date(today);
            end.setHours(23, 59, 59, 999);
            return [startDate, end];
          }
        },
        {
          text: '上周',
          value: () => {
            const today = new Date();
            // 获取上周一
            const dayOfWeek = today.getDay();
            const lastMonday = new Date(today);
            lastMonday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) - 7);
            lastMonday.setHours(0, 0, 0, 0);
            // 获取上周日
            const lastSunday = new Date(lastMonday);
            lastSunday.setDate(lastMonday.getDate() + 6);
            lastSunday.setHours(23, 59, 59, 999);
            return [lastMonday, lastSunday];
          }
        },
        {
          text: '上月',
          value: () => {
            const today = new Date();
            // 获取上月第一天
            const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            firstDay.setHours(0, 0, 0, 0);
            // 获取上月最后一天
            const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
            lastDay.setHours(23, 59, 59, 999);
            return [firstDay, lastDay];
          }
        }
      ]
    };
  },
  methods: {
    // 显示数据
    initData() {
      // 只处理成对的数据
      if (!this.startTime || !this.endTime) {
        this.dateRange = [];
        return;
      }
      let start = new Date(this.startTime);
      let end = new Date(this.endTime);
      this.dateRangeStr = start.format("yyyy-MM-dd") + " 至 " + end.format("yyyy-MM-dd");
      this.dateRange = [start, end];
    },
    changeData() {
      // 日期改变时的回调，将日期返回给父组件
      if (!this.dateRange || this.dateRange.length !== 2) {
        // 清空数据
        this.$emit("update:startTime", "");
        this.$emit("update:endTime", "");
        this.$emit("clear");
        return;
      }
      let start = this.dateRange[0];
      let end = this.dateRange[1];
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      this.$emit("update:startTime", start.format("yyyy-MM-dd hh:mm:ss"));
      this.$emit("update:endTime", end.format("yyyy-MM-dd hh:mm:ss"));
      this.$emit("change", [start, end]);
    }
  }
};
</script>
<style lang="scss" scoped>
.date-range-wrapper {
  width: 100%;
  box-shadow: 0 0 0 1px var(--el-input-border-color, var(--el-border-color)) inset;
  border-radius: var(--el-input-border-radius, var(--el-border-radius-base));
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 1px;

  .prefix {
    white-space: nowrap;
    padding-left: 10px;

    &:after {
      content: ":";
    }

    .default-icon {
      color: var(--yq-font-color-desc);
    }
  }

  :deep(.date-picker-custom) {
    width: 100%;
    box-sizing: border-box;
    box-shadow: none;

    &.is-active {
      box-shadow: none;
    }

    &:hover {
      box-shadow: none;
    }

    .el-range__icon {
      display: none;
    }
  }
}
</style>